import React, { forwardRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { getSurveyUrl } from 'utilities/url'
import { format, formatDistanceToNowStrict, isAfter } from 'date-fns'
import { isofy } from 'utilities/date-time'
import { capitalize } from 'utilities/string'
import { CardListRow, SurveyLink, CardListCell, Symbol, Navigate } from './s'
import Paragraph from 'components/typography/paragraph'
import SurveyFrequencyDescription from 'pages/surveys/components/frequency-description'
import { InlineMessage } from 'components/message'
import SurveyStatus from 'pages/surveys/components/survey-status'
import { getFallbackSymbol } from 'pages/surveys/utilities'
import { Tooltip } from 'components/popover'

const Survey = forwardRef(({ survey = {} }, ref) => {
    const { formatMessage } = useIntl()
    const { dateLocale: locale } = useI18n()

    const title = survey.title ?? formatMessage({
        id: 'employee_satisfaction_chart_label_enps',
        defaultMessage: 'eNPS'
    })

    const getTimeMessages = useGetTimeMessages(survey.nextRunTime)

    return (
        <CardListRow ref={ref}>
            <SurveyLink to={getSurveyUrl(survey)}>
                <CardListCell $cell="symbol">
                    <Symbol
                        symbol={survey?.symbol ?? getFallbackSymbol(survey)}
                        className="hover-effect" />
                </CardListCell>
                <CardListCell $cell="meta">
                    <Paragraph className="name compact">{title}</Paragraph>
                    <Paragraph className="caption small interpoint-divider compact">
                        <span>
                            <FormattedMessage
                                id={(survey.status === 'draft') ?
                                    'status_not_started'
                                    : !!survey.interval ?
                                        'survey_interval_recurring'
                                        : 'survey_interval_onetime'
                                }
                                defaultMessage={(survey.status === 'draft') ?
                                    'Not started yet'
                                    : !!survey.interval ?
                                        'Recurring'
                                        : 'One-time'
                                } />
                        </span>
                        <SurveyFrequencyDescription
                            survey={survey}
                            as="span" />
                    </Paragraph>
                    {(!!survey.nextRunTime && getTimeMessages) && (
                        <>
                            {(survey.status === 'published') && (
                                <Tooltip content={capitalize(format(
                                    isofy(survey.nextRunTime),
                                    formatMessage({
                                        id: 'date_fns_format_weekday_date_time_friendly',
                                        defaultMessage: "EEEE PPP 'at' p"
                                    }),
                                    { locale }
                                ))}>
                                    <InlineMessage
                                        type="info"
                                        message={getTimeMessages.timeNextMessage}
                                        className="compact" />
                                </Tooltip>
                            )}
                            {/* {['active', 'paused'].includes(survey.status) && (
                                <Tooltip content={capitalize(format(
                                    isofy(survey.nextRunTime),
                                    formatMessage({
                                        id: 'date_fns_format_weekday_date_time_friendly',
                                        defaultMessage: "EEEE PPP 'at' p"
                                    }),
                                    { locale }
                                ))}>
                                    <InlineMessage
                                        type="info"
                                        message={getTimeMessages.timeEndsMessage}
                                        className="compact" />
                                </Tooltip>
                            )} */}
                        </>
                    )}
                </CardListCell>
                <CardListCell $cell="status">
                    <SurveyStatus status={survey.status} />
                </CardListCell>
                <CardListCell $cell="navigate">
                    <Navigate size={24} />
                </CardListCell>
            </SurveyLink>
        </CardListRow>
    )
})

const useGetTimeMessages = endTime => {
    const { formatMessage } = useIntl()
    const { dateLocale: locale } = useI18n()

    if(!endTime) return null

    const now = new Date()
    const end = isofy(endTime)
    const ended = isAfter(now, end)

    if(ended) return null

    const formatTimeMessage = (context = 'next') => {
        const message = {
            id: (context === 'next') ?
                'survey_next_message'
                : 'survey_current_run_end_message',
            defaultMessage: (context === 'next') ?
                'Next survey in {time}'
                : 'Current run ends in {time}'
        }
        const time = formatDistanceToNowStrict(end, { locale })

        return formatMessage(message, { time })
    }

    const timeNextMessage = formatTimeMessage('next')
    const timeEndsMessage = formatTimeMessage('ends')

    return { timeNextMessage, timeEndsMessage }
}

export default Survey